/**
 * La zona de Tarifas: las dos colas del catalogo.
 *
 * Las dos existian y las dos solo por terminal. Una cola que hay que abrir una
 * sesion de SSH para mirar es una cola que nadie mira, y estas dos deciden si
 * el buscador tiene algo que devolver.
 *
 * ===========================================================================
 * DOS COLAS, DOS TRABAJOS, Y SOLO UNA TIENE BOTON
 *
 * FIRMAR UN SHEET se hace aqui. Es la firma humana de la [Invariante 8]: hasta
 * que alguien responde de ese documento, sus tarifas no llegan a ninguna
 * cotizacion. El boton solo aparece en los documentos que la base aceptaria
 * --`apto_para_verificar`--; en los demas se pinta el MOTIVO, que es lo unico
 * que dice que hay que arreglar. Un boton deshabilitado sin explicacion manda a
 * probar otra vez.
 *
 * CONFIRMAR UN ALIAS no se hace aqui, y la pantalla lo dice. Es un UPDATE sobre
 * el catalogo y eso se escribe como migracion [CLAUDE.md]: firmar dice "he
 * leido este documento", mientras que confirmar un alias dice "de ahora en
 * adelante esta palabra significa este puerto", que es una afirmacion sobre el
 * catalogo entero y sobrevive a quien la hizo. La cola se ve --con su impacto,
 * que es lo que dice cual atender antes-- y debajo dice por donde se despacha.
 * Una cola visible sin boton es honesta; un boton sin la explicacion no.
 *
 * ===========================================================================
 * LOS DOS ESTADOS DE UN ALIAS SON DOS TRABAJOS, NO DOS GRADOS
 *
 * PROPUESTO tiene destino y solo falta la firma: un typo, un puerto que el
 * maestro tiene con otro nombre. Se despacha en un minuto.
 *
 * SIN_RESOLVER no apunta a nada, o apunta al centinela REVISAR. Firmarlo no lo
 * arregla: hace falta una decision de negocio o alguien que conozca el dominio.
 *
 * Se pintan separados por eso, igual que los imprime `npm run revisar-alias`:
 * quien atiende la cola decide primero cual de los dos puede hacer hoy.
 */

import type { AliasPendiente, SheetPendiente } from '../api';
import { el, literal } from '../dom';
import { formatearFecha, type Idioma } from '../formato';
import type { Traductor } from '../i18n';

export interface OpcionesTarifas {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly sheets: readonly SheetPendiente[];
  readonly alias: readonly AliasPendiente[];
  readonly cargando: boolean;
  /** El id del sheet que se esta firmando ahora mismo, o `null`. */
  readonly firmando: string | null;
  /** Lo que acaba de pasar, en una linea. */
  readonly aviso: string | null;
  readonly onFirmar: (id: string) => void;
}

export function pintarTarifas(o: OpcionesTarifas): HTMLElement {
  const { t } = o;

  return el('div', { clase: 'zona-tarifas' }, [
    el('header', { clase: 'zona-cabecera' }, [
      el('h2', { texto: t('zona.tarifas.nombre') }),
      el('p', { clase: 'tenue', texto: t('tarifas.intro') }),
    ]),

    o.aviso === null ? null : el('p', { clase: 'aviso aviso-ok', texto: o.aviso }),

    o.cargando ? el('p', { clase: 'tenue', texto: t('app.cargando') }) : null,

    pintarColaSheets(o),
    pintarColaAlias(o),
  ]);
}

// ---------------------------------------------------------------------------
// Cola de verificacion
// ---------------------------------------------------------------------------

function pintarColaSheets(o: OpcionesTarifas): HTMLElement {
  const { t } = o;

  const filas = o.sheets.map((s) =>
    el('tr', { clase: s.apto_para_verificar ? 'fila-lista' : 'fila-bloqueada' }, [
      el('td', {}, [
        // El asunto y el message_id son literales del correo: se pintan tal
        // como llegaron [CLAUDE.md, Idioma, categoria 2].
        literal(s.asunto) ??
          el('span', { clase: 'tenue', texto: t('tarifas.sinAsunto') }),
        s.message_id === null
          ? null
          : el('code', { clase: 'tenue mensaje-id', texto: s.message_id }),
      ]),
      el('td', {}, [
        literal(s.proveedor) ??
          // [Tema 6] No se pudo identificar la empresa del correo. Se dice.
          el('span', { clase: 'tenue', texto: t('tarifas.sinProveedor') }),
      ]),
      el('td', {}, [
        el('span', { clase: 'chip chip-estado', texto: t(`estadoParseo.${s.estado}`) }),
      ]),
      el('td', { clase: 'celda-numero', texto: String(s.records) }),
      el('td', {
        clase: `celda-numero ${s.records_sin_flete > 0 ? 'celda-alerta' : ''}`,
        texto: String(s.records_sin_flete),
      }),
      el('td', { texto: formatearFecha(s.recibido_en.slice(0, 10), o.idioma) }),
      el('td', {}, [
        s.apto_para_verificar
          ? el('button', {
              clase: 'boton boton-primario',
              texto:
                o.firmando === s.rate_sheet_id
                  ? t('tarifas.firmando')
                  : t('tarifas.firmar'),
              attrs:
                o.firmando === null
                  ? { type: 'button' }
                  : { type: 'button', disabled: 'disabled' },
              onClick: () => {
                o.onFirmar(s.rate_sheet_id);
              },
            })
          : // No un boton apagado: el motivo. Es lo unico accionable.
            el('span', {
              clase: 'tenue motivo-bloqueo',
              texto: s.motivo_bloqueo ?? t('tarifas.bloqueadoSinMotivo'),
            }),
      ]),
    ]),
  );

  return el('section', { clase: 'panel panel-cola' }, [
    el('h3', { texto: t('tarifas.sheets.titulo') }),
    el('p', { clase: 'tenue', texto: t('tarifas.sheets.explicacion') }),

    o.sheets.length === 0
      ? el('p', { clase: 'tenue', texto: t('tarifas.sheets.vacia') })
      : el('table', { clase: 'tabla-resultados tabla-cola' }, [
          el('thead', {}, [
            el('tr', {}, [
              el('th', { texto: t('tarifas.sheets.documento') }),
              el('th', { texto: t('tarifas.sheets.proveedor') }),
              el('th', { texto: t('tarifas.sheets.estado') }),
              el('th', { clase: 'celda-numero', texto: t('tarifas.sheets.tarifas') }),
              el('th', { clase: 'celda-numero', texto: t('tarifas.sheets.sinFlete') }),
              el('th', { texto: t('tarifas.sheets.recibido') }),
              el('th', {}),
            ]),
          ]),
          el('tbody', {}, filas),
        ]),
  ]);
}

// ---------------------------------------------------------------------------
// Cola de alias
// ---------------------------------------------------------------------------

function pintarColaAlias(o: OpcionesTarifas): HTMLElement {
  const { t } = o;

  const propuestos = o.alias.filter((a) => a.estado === 'PROPUESTO');
  const sinResolver = o.alias.filter((a) => a.estado === 'SIN_RESOLVER');

  return el('section', { clase: 'panel panel-cola' }, [
    el('h3', { texto: t('tarifas.alias.titulo') }),
    el('p', { clase: 'tenue', texto: t('tarifas.alias.explicacion') }),

    o.alias.length === 0
      ? el('p', { clase: 'tenue', texto: t('tarifas.alias.vacia') })
      : el('div', {}, [
          grupoAlias(t, o.idioma, 'PROPUESTO', propuestos),
          grupoAlias(t, o.idioma, 'SIN_RESOLVER', sinResolver),
          // Por donde se despacha. Va debajo de la tabla y no en un tooltip:
          // es la respuesta a "y como confirmo esto", que es la primera
          // pregunta de quien mira esta cola.
          el('p', {
            clase: 'aviso aviso-neutro',
            texto: t('tarifas.alias.comoSeDespacha'),
          }),
        ]),
  ]);
}

function grupoAlias(
  t: Traductor,
  idioma: Idioma,
  estado: 'PROPUESTO' | 'SIN_RESOLVER',
  filas: readonly AliasPendiente[],
): HTMLElement | null {
  if (filas.length === 0) return null;

  return el('div', { clase: 'grupo-alias' }, [
    el('h4', { texto: t(`tarifas.alias.estado.${estado}`) }),
    el('p', { clase: 'tenue', texto: t(`tarifas.alias.ayuda.${estado}`) }),
    el('table', { clase: 'tabla-resultados tabla-alias' }, [
      el('thead', {}, [
        el('tr', {}, [
          el('th', { texto: t('tarifas.alias.tipo') }),
          el('th', { texto: t('tarifas.alias.alias') }),
          el('th', { texto: t('tarifas.alias.resuelveA') }),
          el('th', { texto: t('tarifas.alias.proveedor') }),
          // La columna que decide a cual atender antes. [punto I]
          el('th', { clase: 'celda-numero', texto: t('tarifas.alias.bloqueadas') }),
        ]),
      ]),
      el(
        'tbody',
        {},
        filas.map((a) =>
          el('tr', { clase: a.expansiones_bloqueadas > 0 ? 'fila-espera' : undefined }, [
            el('td', { texto: t(`tarifas.alias.catalogo.${a.catalogo}`) }),
            el('td', {}, [
              // El alias es literal del proveedor: tal como llego, en el
              // alfabeto que sea.
              literal(a.alias, 'literal codigo-alias'),
              a.pais_hint === null
                ? null
                : el('span', { clase: 'tenue', texto: ` · ${a.pais_hint}` }),
              a.idioma === null
                ? null
                : el('span', { clase: 'tenue', texto: ` · ${a.idioma}` }),
            ]),
            el('td', {}, [
              a.resuelve_a === null
                ? el('span', { clase: 'tenue', texto: t('tarifas.alias.aNada') })
                : a.resuelve_a === 'REVISAR'
                  ? el('span', { clase: 'tenue', texto: t('tarifas.alias.aRevisar') })
                  : el('code', { texto: a.resuelve_a }),
              a.confianza === null
                ? null
                : el('span', {
                    clase: 'tenue',
                    texto: ` · ${t('tarifas.alias.confianza')} ${formatearConfianza(a.confianza, idioma)}`,
                  }),
            ]),
            el('td', {}, [
              literal(a.proveedor) ??
                el('span', { clase: 'tenue', texto: t('tarifas.alias.global') }),
            ]),
            // CERO ES CERO, NO UN GUION.
            //
            // El guion es el marcador de AUSENTE en esta pantalla [formato.ts],
            // asi que pintarlo aqui hacia que "este alias no bloquea nada" y
            // "no se sabe cuantas bloquea" se vieran igual. Es [H59] al reves:
            // alli el problema era enseñar un cero donde falta un dato, y aqui
            // era enseñar una ausencia donde el dato es cero.
            //
            // Con cero se pinta el cero, en tenue: la columna existe para
            // encontrar los que SI bloquean, y los que no tienen que poder
            // descartarse de un vistazo sin dejar de ser una lectura.
            el('td', { clase: 'celda-numero' }, [
              a.expansiones_bloqueadas === 0
                ? el('span', { clase: 'tenue', texto: '0' })
                : el('span', {
                    texto: `${String(a.expansiones_bloqueadas)} (${String(a.filas_afectadas)})`,
                  }),
            ]),
          ]),
        ),
      ),
    ]),
  ]);
}

/** La confianza como porcentaje. Es un `numeric(4,3)` que llega como cadena, y
 *  no es dinero: se puede convertir sin romper la regla de `decimal.js`. */
function formatearConfianza(v: string, idioma: Idioma): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return new Intl.NumberFormat(idioma, { style: 'percent' }).format(n);
}
